import React, { useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, TextField,
} from '@mui/material';
import { ActivationResult, MasterDataStatusFilter } from '../../interfaces/masterDataStatus.interface';

/** FW-1: Active / Inactive chip shared by the customer, supplier and store lists and detail headers. A missing flag reads as active. */
export const ActiveStatusChip: React.FC<{ active?: boolean; reason?: string | null }> = ({ active, reason }) => (
  <Chip
    size="small"
    label={active === false ? 'Inactive' : 'Active'}
    color={active === false ? 'default' : 'success'}
    variant={active === false ? 'outlined' : 'filled'}
    title={active === false && reason ? reason : undefined}
    data-testid={active === false ? 'status-chip-inactive' : 'status-chip-active'}
  />
);

/** The status filter cell of a list toolbar: a flex cell, never a fixed width (CLAUDE.md filter convention). */
export const StatusFilterField: React.FC<{ value: MasterDataStatusFilter; onChange: (v: MasterDataStatusFilter) => void }> = ({ value, onChange }) => (
  <TextField
    select
    size="small"
    label="Status"
    data-help="common.status.field.filter"
    value={value}
    onChange={(e) => onChange(e.target.value as MasterDataStatusFilter)}
    sx={{ flex: '1 1 160px', maxWidth: 220 }}
    inputProps={{ 'data-testid': 'status-filter' }}
  >
    <MenuItem value="ACTIVE">Active</MenuItem>
    <MenuItem value="INACTIVE">Inactive</MenuItem>
    <MenuItem value="ALL">All</MenuItem>
  </TextField>
);

const OPEN_ITEM_LABELS: Record<string, string> = {
  invoices: 'open invoices',
  quotations: 'open quotations',
  salesOrders: 'open sales orders',
  purchaseOrders: 'open purchase orders',
  unpaidBills: 'unpaid bills',
  openAdvances: 'open advances',
  recurringTemplates: 'active recurring templates',
};

/** "2 open invoices, 1 unpaid bill" for the non-zero counts; empty string when nothing is open. */
export const describeOpenItems = (items?: Record<string, number>): string =>
  Object.entries(items ?? {})
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${n} ${OPEN_ITEM_LABELS[k] ?? k}`)
    .join(', ');

interface DeactivateDialogProps {
  open: boolean;
  /** "customer" | "supplier" | "store" */
  noun: string;
  name: string;
  /** Resolves with the server result (the dialog then shows the open-item counts); rejects to keep the dialog open. */
  onDeactivate: (reason: string) => Promise<ActivationResult>;
  /** Called as soon as the server confirms a deactivation, before the dialog is closed, so the page behind can refresh its status at once. */
  onDeactivated?: (result: ActivationResult) => void;
  /** Called when the dialog is dismissed; `changed` is true when a deactivation happened. */
  onClose: (changed: boolean) => void;
}

/**
 * Confirm dialog with an optional reason. Customers and suppliers are warned, not refused: once deactivated the
 * dialog lists what is still open (the server counts it in the same call), because old documents keep working.
 */
export const DeactivateDialog: React.FC<DeactivateDialogProps> = ({ open, noun, name, onDeactivate, onDeactivated, onClose }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ActivationResult | null>(null);

  const close = () => {
    const changed = result?.changed ?? false;
    setReason('');
    setResult(null);
    onClose(changed);
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await onDeactivate(reason);
      setResult(res);
      if (res.changed) onDeactivated?.(res);
    } catch {
      // the caller reports the error (for example a store that still holds stock); the dialog stays open
    } finally {
      setBusy(false);
    }
  };

  const open_ = describeOpenItems(result?.openItems);
  return (
    <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="xs" data-help-page="common.deactivate.page">
      <DialogTitle>{result ? `${name} is now inactive` : `Deactivate this ${noun}?`}</DialogTitle>
      <DialogContent>
        {!result ? (
          <>
            <DialogContentText component="div" sx={{ mb: 2 }}>
              <strong>{name}</strong> will no longer appear when you create a new document. Existing documents, receipts,
              payments, reports and history are not affected, and you can activate it again at any time.
            </DialogContentText>
            <TextField
              label="Reason (optional)"
              data-help="common.deactivate.field.reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              inputProps={{ maxLength: 500 }}
            />
          </>
        ) : (
          <Box>
            {open_ ? (
              <Alert severity="warning" data-testid="open-items-warning">
                Still open: {open_}. They keep working and can be completed as usual.
              </Alert>
            ) : (
              <Alert severity="success">Nothing is open for this {noun}.</Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!result ? (
          <>
            <Button onClick={close} disabled={busy} data-help="common.deactivate.cancel">Cancel</Button>
            <Button onClick={confirm} color="warning" variant="contained" disabled={busy} data-help="common.deactivate.confirm">Deactivate</Button>
          </>
        ) : (
          <Button onClick={close} variant="contained" data-help="common.deactivate.close">Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

/** Import (opens /data-import on the kind) and Export CSV buttons for a master-data list header. */
export const ImportExportButtons: React.FC<{
  kind: 'CUSTOMERS' | 'SUPPLIERS';
  onImport: () => void;
  onExport: () => void | Promise<void>;
}> = ({ onImport, onExport }) => {
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Button variant="outlined" onClick={onImport} data-help="common.masterData.import">Import</Button>
      <Button
        variant="outlined"
        disabled={busy}
        data-help="common.masterData.export"
        onClick={async () => { setBusy(true); try { await onExport(); } finally { setBusy(false); } }}
      >
        Export CSV
      </Button>
    </>
  );
};

/** Saves a blob under a file name (the Export CSV buttons). */
export const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
